/**
 * Supplements nach Form (16.10.2026): Pulver, Kapseln/Tabletten, Öl/Flüssiges
 * — jede Form mit den Einheiten, in denen man sie wirklich abmisst.
 *
 * Die Menge je Einnahme schreibt man von der Packung ab („1 Kapsel“,
 * „5 g“). Die App rechnet nichts aus und schlägt nichts vor: Sie
 * protokolliert, sie verordnet nicht.
 */

import type { Schedule } from './reminders.js';
import { weekdayOf } from './schedule.js';

export const SUPPLEMENT_FORMS = ['powder', 'capsule', 'liquid'] as const;
export type SupplementForm = (typeof SUPPLEMENT_FORMS)[number];

/** Einheiten je Form — die erste ist die Vorgabe. */
export const SUPPLEMENT_UNITS = {
  powder: ['g', 'scoop'],
  capsule: ['piece'],
  liquid: ['ml', 'drop', 'tsp'],
} as const satisfies Record<SupplementForm, readonly string[]>;

export type SupplementUnit = (typeof SUPPLEMENT_UNITS)[SupplementForm][number];

export const ALL_SUPPLEMENT_UNITS: readonly SupplementUnit[] = [
  ...new Set(Object.values(SUPPLEMENT_UNITS).flat()),
] as SupplementUnit[];

export function isSupplementForm(value: unknown): value is SupplementForm {
  return typeof value === 'string' && (SUPPLEMENT_FORMS as readonly string[]).includes(value);
}

export function unitsFor(form: SupplementForm): readonly SupplementUnit[] {
  return SUPPLEMENT_UNITS[form];
}

/** Passt die Einheit zur Form? Ohne Form ist jede (auch alte Freitext-)Einheit recht. */
export function unitFitsForm(form: SupplementForm | null, unit: string | null): boolean {
  if (form === null || unit === null) return true;
  return (SUPPLEMENT_UNITS[form] as readonly string[]).includes(unit);
}

const UNIT_LABELS: Record<'de' | 'en', Record<SupplementUnit, [string, string]>> = {
  de: {
    g: ['g', 'g'],
    scoop: ['Messlöffel', 'Messlöffel'],
    piece: ['Stück', 'Stück'],
    ml: ['ml', 'ml'],
    drop: ['Tropfen', 'Tropfen'],
    tsp: ['TL', 'TL'],
  },
  en: {
    g: ['g', 'g'],
    scoop: ['scoop', 'scoops'],
    piece: ['piece', 'pieces'],
    ml: ['ml', 'ml'],
    drop: ['drop', 'drops'],
    tsp: ['tsp', 'tsp'],
  },
};

/**
 * Anzeigename einer Einheit. Ältere Supplements tragen Freitext („µg“) —
 * der bleibt, wie er ist, statt still zu verschwinden.
 */
export function unitLabel(unit: string | null, language: 'de' | 'en', amount = 1): string {
  if (unit === null) return '';
  const labels = UNIT_LABELS[language][unit as SupplementUnit];
  if (!labels) return unit;
  return amount === 1 ? labels[0] : labels[1];
}

/** „1 Stück“, „5 g“, „10 Tropfen“ — oder leer ohne Menge. */
export function describeAmount(
  dose: number | null,
  unit: string | null,
  language: 'de' | 'en' = 'de',
): string {
  if (dose === null) return '';
  const number = dose.toLocaleString(language === 'de' ? 'de-DE' : 'en-GB', {
    maximumFractionDigits: 2,
  });
  const label = unitLabel(unit, language, dose);
  return label ? `${number} ${label}` : number;
}

/** Steht das Supplement an diesem Kalendertag im Plan? (Wochentage, Monate) */
export function scheduledOn(schedule: Schedule, date: string): boolean {
  if (schedule.months && schedule.months.length > 0) {
    if (!schedule.months.includes(Number(date.slice(5, 7)))) return false;
  }
  if (schedule.weekdays && schedule.weekdays.length > 0) {
    if (!schedule.weekdays.includes(weekdayOf(date))) return false;
  }
  return true;
}

/** Strichcode einer Packung: 8 bis 14 Ziffern (EAN-8, EAN-13, UPC). */
export function isPackageBarcode(text: string): boolean {
  return /^\d{8,14}$/.test(text);
}
